import { __awaiter } from "tslib";
import { useCallback, useEffect, useState } from 'react';
import { useConfig } from '../../../../context';
import { Log } from '../../../../sentry';
import { getErrorMessage, STATIC_EXCEPTION_CATEGORY, StaticException } from '@shared/error';
import { getUrlArray, loadData } from './utils';
import { getLocale } from '../../../../utils';
import { localizeJson } from '../localization';
export function useStatic(props) {
    const { path, fallback, contentRepo = true, responseType, skip = false } = props;
    const { global: { companyInfo: { iataCode } } } = useConfig();
    const [data, setData] = useState(undefined);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(undefined);
    const logStaticError = useCallback((message, extraData) => {
        Log({
            category: STATIC_EXCEPTION_CATEGORY,
            level: 'error',
            message,
            data: Object.assign({ path,
                fallback }, extraData)
        });
    }, [path, fallback]);
    const handleSetError = (error) => {
        setError(error instanceof Error ? error : new Error(getErrorMessage(error)));
    };
    useEffect(() => {
        if (!path || skip) {
            setData(undefined);
            setError(undefined);
            setLoading(false);
            return undefined;
        }
        const controller = new AbortController();
        const { signal } = controller;
        const run = () => __awaiter(this, void 0, void 0, function* () {
            setData(undefined);
            setError(undefined);
            setLoading(true);
            let urls;
            try {
                urls = getUrlArray(path, fallback, contentRepo, iataCode);
            }
            catch (error) {
                if (signal.aborted) {
                    setLoading(false);
                    return;
                }
                handleSetError(error);
                logStaticError('useStatic: неверная конфигурация URL', {
                    errorMessage: getErrorMessage(error)
                });
                setLoading(false);
                return;
            }
            for (const url of urls) {
                try {
                    const data = yield loadData(url, signal, responseType);
                    if (signal.aborted) {
                        setLoading(false);
                        return;
                    }
                    const localizedData = responseType === 'json' ? localizeJson(data, getLocale()) : data;
                    setData(localizedData);
                    setLoading(false);
                    return;
                }
                catch (error) {
                    if (signal.aborted) {
                        setLoading(false);
                        return;
                    }
                    logStaticError(`useStatic: ошибка загрузки по адресу ${url}`, {
                        resultUrl: url,
                        errorMessage: getErrorMessage(error)
                    });
                }
            }
            if (!signal.aborted) {
                const error = new StaticException('useStatic: не удалось загрузить ресурс');
                handleSetError(error);
                logStaticError(error.message);
                setLoading(false);
            }
        });
        run();
        return () => {
            controller.abort();
        };
    }, [path, fallback, contentRepo, responseType, iataCode, logStaticError, skip]);
    return {
        data,
        loading,
        error
    };
}
